import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { ActivatedRoute, Router } from "@angular/router";
import { BehaviorSubject, Observable, combineLatest, merge, of, throwError } from "rxjs";
import { catchError, map, mergeMap, switchMap } from "rxjs/operators";

import { TranslateService } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";

import { CoreMenuService } from "@core/components/core-menu/core-menu.service";
import { InsightsService } from "@core/services/Insights.service";

import { User } from "@common/models";

import {
  AppPermissionsService,
  MenuService,
  StatusService,
} from "@common/services";

import { CustomSwalService } from "app/helpers";
import { environment } from "environments/environment";
import { IdleTimeoutService } from "@core/services";

// Llave del localStorage que almacena la empresa seleccionada por el usuario
const CURRENT_COMPANY_ID_KEY = "currentCompanyId";

@Injectable({ providedIn: "root" })
export class AuthenticationService {
  //private
  private currentUserSubject: BehaviorSubject<User>;
  private currentTokenSubject: BehaviorSubject<string>;
  private currentCompanyIdSubject: BehaviorSubject<number>;
  private renewingSessionSubject: BehaviorSubject<boolean>;

  /**
   *
   * @param {HttpClient} _http
   */
  constructor(
    private _route: ActivatedRoute,
    private _permissionService: AppPermissionsService,
    private _coreMenuService: CoreMenuService,
    private _swalService: CustomSwalService,
    private _http: HttpClient,
    private _insightService: InsightsService,
    private _menuService: MenuService,
    private _router: Router,
    private _statusService: StatusService,
    private _toastrService: ToastrService,
    private _translateService: TranslateService,
    private _idleTimeoutService: IdleTimeoutService
  ) {
    this.currentUserSubject = new BehaviorSubject(null);
    this.currentCompanyIdSubject = new BehaviorSubject(
      this.getStoredCompanyId()
    );
    this.currentTokenSubject = new BehaviorSubject<string>(
      localStorage.getItem("currentToken")
    );
    this.renewingSessionSubject = new BehaviorSubject<boolean>(false);
  }

  /**
   * Indica si el token actual sirve para llamar a las apis, es decir si existe
   * y todavía no ha vencido
   */
  public get hasValidToken(): boolean {
    return this.isAuthenticated() && !this.isTokenExpired();
  }

  /**
   * Indica si hay una renovación de sesión en curso. Mientras lo esté, un 401 de
   * las apis es esperable y las peticiones deben esperar al token nuevo
   */
  public get isRenewingSession(): boolean {
    return this.renewingSessionSubject.value;
  }

  /**
   * Marca el inicio de la renovación de la sesión, previo a pedirle un token
   * nuevo al proveedor de identidad
   */
  public beginSessionRenewal(): void {
    this.renewingSessionSubject.next(true);
  }

  /**
   * Marca el fin de la renovación de la sesión, ya sea porque se obtuvo el token
   * o porque se descartó el intento
   */
  public endSessionRenewal(): void {
    this.renewingSessionSubject.next(false);
  }

  /**
   * Emite true cuando la sesión queda resuelta: cuando hay un token válido en
   * memoria o cuando ya no hay ninguna renovación en curso que esperar
   */
  public get onSessionResolved(): Observable<boolean> {
    return combineLatest([
      this.currentTokenSubject,
      this.renewingSessionSubject,
    ]).pipe(map(() => this.hasValidToken || !this.isRenewingSession));
  }

  /**
   * Función que obtiene la empresa seleccionada por el usuario desde el localStorage
   *
   * @returns El id de la empresa almacenada, o null si no existe
   */
  private getStoredCompanyId(): number {
    const storedCompanyId = Number(localStorage.getItem(CURRENT_COMPANY_ID_KEY));

    return storedCompanyId ? storedCompanyId : null;
  }

  // getter: currentUserValue
  public get currentUserValue(): User {
    return this.currentUserSubject.value;
  }

  // getter: currentCompanyIdValue
  public get currentCompanyIdValue(): number {
    return this.currentCompanyIdSubject.value;
  }

  // getter: event current user value changed
  public get onUserValueChanged(): Observable<User> {
    return this.currentUserSubject.asObservable();
  }

  // getter: event current company value changed
  public get onCompanyIdChanged(): Observable<number> {
    return this.currentCompanyIdSubject.asObservable();
  }

  public get token(): string {
    return this.currentTokenSubject.value;
  }

  /**
   * User login
   *
   * @param username
   * @param password
   * @returns user
   */
  login(username: string, password: string) {
    const urlEndPoint: string = `${environment.securityApiUrl}/api/token/1`;
    const credentials = btoa(`${username}:${password}`);
    const httpHeaders = new HttpHeaders({
      Authorization: `Basic ${credentials}`,
    });

    return this._http.post(urlEndPoint, null, {
      headers: httpHeaders,
      responseType: "text",
    });
  }

  /**
   * Función que verifica la autenticacion doble
   */
  verificationCode(token: string, code: number): Observable<any> {
    const urlEndPoint: string = `${environment.securityApiUrl}/api/token/verification/${code}`;
    const httpHeaders = new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });

    return this._http.post<any>(urlEndPoint, null, {
      headers: httpHeaders
    });
  }

  /**
   * Función que obtiene y almacena el usuario autenticado
   */
  saveUser(): void {

    const httpHeaders = new HttpHeaders({
      Authorization: `Bearer ${this.token}`,
    });

    this._http
        .get<User>(`${environment.securityApiUrl}/api/users/logged`, {
          headers: httpHeaders,
        })
        .pipe(
            switchMap((securityUser: User) => {

              return this._http
                  .get<User>(
                      `${environment.commonApiUrl}/api/users/${securityUser.id}`,
                      { headers: httpHeaders }
                  )
                  .pipe(
                      map((commonUser: User) => {

                        // Tomamos superUser desde common
                        securityUser.superUser =
                            commonUser?.superUser === true;

                        return securityUser;
                      }),

                      // Si falla common, no bloqueamos el login
                      catchError(() => {

                        securityUser.superUser = false;

                        return of(securityUser);
                      })
                  );
            }),

            catchError((e) => {
              return throwError(e);
            })
        )
        .subscribe(
            (resp) => {

              // Registrar superusuario antes de notificar
              this._permissionService.setSuperUser(
                  resp?.superUser === true
              );

              // Notify
              this.currentUserSubject.next(resp);

              this._insightService.setUserContext(resp);
            },
            (error) => {
              if (error) {
                this._translateService
                    .get([
                      'COMMON.ERROR_INTECEPTOR.GET_LOGGED_USER'
                    ])
                    .subscribe(value => {

                      this._swalService.showErrorMsg(
                          value[
                              'COMMON.ERROR_INTECEPTOR.GET_LOGGED_USER'
                              ]
                      );
                    });
              }
            }
        );
  }

  /**
   * Función que sirve para guardar el usuario
   * @param idToken Indica el token de autenticación del usuario
   */
  saveToken(idToken: string): void {
    localStorage.setItem("currentToken", idToken);
    // notify
    this.currentTokenSubject.next(idToken);
  }

  /**
   * Función para obtener el payload del token
   * @param {string} accessToken Indica el token de autenticación del usuario
   * @returns {any} Devuelve si el payload del token
   */
  getTokenPayload(accessToken: string): any {
    if (accessToken) {
      return JSON.parse(atob(accessToken.split(".")[1]));
    }
    return null;
  }

  /**
   * Función que devuelve si el usuario esta o no autenticado
   * @returns {boolean} Devuelve si esta autenticado o no
   */
  isAuthenticated(): boolean {
    let payload = this.getTokenPayload(this.token);
    if (
      payload != null &&
      payload.sub &&
      payload.sub.length > 0
    ) {
      return true;
    }
    return false;
  }

  /**
   * Función que sirve para verificar si el token esta activo o inactivo
   * @returns {boolean} Devuelve si el token ha expirado o no
   */
  isTokenExpired(): boolean {
    let token = this.token;
    let payload = this.getTokenPayload(token);
    // Sin token no hay nada vigente que validar
    if (!payload) return true;
    let now = new Date().getTime() / 1000;
    if (payload.exp < now) {
      return true;
    }
    return false;
  }

  /**
   * Función para almacenar el usuario y el token.
   * También realiza la carga de menú, permisos y estados
   * @param {string} token Indica el token de autenticación del usuario
   * @param {string} authType Indica el modo de autenticación del usuario
   */
  saveUserToken(token: string, authType: string): void {
    // Almacena el tipo de autenticación
    localStorage.setItem("auth-type", authType);

    // Guarda el token generado
    this.saveToken(token);

    // Realiza una subscripción cuando encuentre un cambio en el token
    // Registra el menú, los permisos y los estados en memoria
    this.registerPermissionsAndStatus();

    // Obtiene el payload del token
    const payload = this.getTokenPayload(token);
    // Obtiene los segundos del timestamp de la fecha actual
    const now = new Date().getTime() / 1000;

    // Obtiene los segundos entre la fecha de expiración que tendrá el token y la fecha actual
    const seconds = payload.exp - now;

    // Agrega los segundos del tiempo que llevará para vencer el token
    this._idleTimeoutService.timeoutSeconds = seconds;

    // Inicia el timer para mostrar que la sesión se irá a vencer
    this._idleTimeoutService.startTimer();

    // Muestra el mensaje de bienvenida
    this.showWelcomeMessage();

    const returnUrl = this._route.snapshot.queryParams["returnUrl"] || 
      (window.location.pathname.includes("login") ? "/" : window.location.pathname) || "/";
    
    this._router.navigateByUrl(decodeURI(returnUrl));
  }

  /**
   * Función que registra los permisos, los menús y los estados en memoria
   */
  registerPermissionsAndStatus(): void {

    // Se valida que el usuario actual este nulo previo a cargar nuevamente los permisos
    if (!this.currentUserValue){
      // Obtiene y almacena el usuario
      this.saveUser();

      // Obtiene y almacena la empresa
      this.saveCompany();

      // Registra el lenguaje del explorador del usuario
      this.registerUserLanguage();

      // Espera a tener información sobre el usuario antes de obtener los permisos
      this.onUserValueChanged.subscribe(resp => {
        if (resp != null){
          this.reloadPermissionsMenuStatus();
        }
      });

    }

  }

  /**
   * Función que recarga los menús, permisos y estados de la empresa activa.
   * Las peticiones viajan con el header 'cid' de la empresa actual, por lo que
   * al cambiar de empresa se obtiene la información correspondiente a la misma.
   */
  private reloadPermissionsMenuStatus(): void {
    // Aplica los menus que tiene del usuario autenticado
    this._menuService.getMenuItems(this.currentUserValue?.superUser === true).subscribe((response) => {
      // Se desregistra el menú
      this._coreMenuService.unregister('main');

      // Register the menu to the menu service
      this._coreMenuService.register("main", response);

      // Set the main menu as our current menu
      this._coreMenuService.setCurrentMenu("main");
    });

    // Obtiene los permisos al menú del usuario
    this._permissionService.register();

    // Registra todos los estado en la memoria de la aplicación
    this._statusService.register();
  }

  /**
   * Función que cambia la empresa activa del usuario y recarga su información
   *
   * @param companyId Entero que indica el id de la nueva empresa activa
   */
  switchCompany(companyId: number): void {
    // Valida que sea una empresa distinta a la actual
    if (!companyId || companyId === this.currentCompanyIdValue) return;

    // Almacena la empresa seleccionada para conservarla al recargar la aplicación
    localStorage.setItem(CURRENT_COMPANY_ID_KEY, `${companyId}`);

    // Notifica la nueva empresa, a partir de aquí las peticiones viajan con el nuevo 'cid'
    this.currentCompanyIdSubject.next(companyId);

    // Recarga los menús, permisos y estados de la nueva empresa
    this.reloadPermissionsMenuStatus();
  }


  /**
   * Función que obtiene el lenguaje del explorador del usuario
   */
  registerUserLanguage(): void {
    // Usa el idioma guardado por el usuario; si no existe, usa el del navegador
    const savedConfig = JSON.parse(localStorage.getItem('config'));
    const savedLang = savedConfig?.app?.appLanguage;

    if (savedLang && ['en', 'es'].includes(savedLang)) {
      this._translateService.use(savedLang);
    } else {
      const browserLang = this._translateService.getBrowserLang();
      this._translateService.use(browserLang.match(/en|es/) ? browserLang : 'es');
    }
  }

  /**
   * Función que muestra el mensaje de bienvenida
   */
  showWelcomeMessage(): void {
    // Se obtienen los eventos cuando cambia de usuario y cuando cambia de lenguaje
    const onUserChanged = this.onUserValueChanged;
    const onLanguageChanged = this._translateService.onTranslationChange;
    
    // Se agregan a una sola variable los eventos
    const events = {
      onUserChanged,
      onLanguageChanged
    };

    // Se concatenan los evnetos en uno solo
    of(events)
    .pipe (
      mergeMap(ev => {
          return merge(...Object.keys(ev).map(k => ev[k]));
      })
    ).subscribe(resp => {
      if(this.currentUserValue && this._translateService.translations){
        this._translateService.get(['PAGES.LOGIN.MESSAGES.WELCOME', 'PAGES.LOGIN.MESSAGES.SUCCESS']).subscribe(value => {
          this._swalService.showToastMsg(`${value['PAGES.LOGIN.MESSAGES.SUCCESS']}`, `${value['PAGES.LOGIN.MESSAGES.WELCOME']} ${this.currentUserValue.person.name}!`, 'success', 5000);
        });
      }
    });
  }

  /**
   * Función que obtiene y almacena la empresa del usuario
   */
  saveCompany(): void {

    const httpHeaders = new HttpHeaders({
      Authorization: `Bearer ${this.token}`,
    });

    this._http
      .get<User>(`${environment.securityApiUrl}/api/company/logged`, {
        headers: httpHeaders,
      })
      .pipe(
        map((response: any) => response as number),
        catchError((e) => {
          return throwError(e);
        })
      )
      .subscribe(
        (resp) => {
          // Conserva la empresa seleccionada por el usuario sobre la empresa por defecto del token
          const storedCompanyId = this.getStoredCompanyId();

          // notify
          this.currentCompanyIdSubject.next(storedCompanyId ? storedCompanyId : resp);
        },
        (error) => {
          if (error) {
            this._translateService.get(['COMMON.ERROR_INTECEPTOR.GET_LOGGED_COMPANY']).subscribe(value => {
              this._swalService.showErrorMsg(value['COMMON.ERROR_INTECEPTOR.GET_LOGGED_COMPANY']);
            });
          }
        }
      );
  }

  /**
   * User logout
   *
   */
  logout(): void {
    // Limpia permisos y estado de superusuario de la sesión anterior
    this._permissionService.unregister();

    // remove user from local storage to log user out
    localStorage.removeItem("currentToken");
    localStorage.removeItem("auth-type");
    localStorage.removeItem("userToken");
    localStorage.removeItem("poweruser");
    localStorage.removeItem(CURRENT_COMPANY_ID_KEY);
    // notify
    this.currentUserSubject.next(null);
    this.currentTokenSubject.next(null);
    this.currentCompanyIdSubject.next(null);
    // Ya no hay sesión que renovar, se liberan las peticiones que estén esperando
    this.endSessionRenewal();
  }
  
}
